import { computed, Directive, input, signal } from '@angular/core';
import { BrnButton } from '@spartan-ng/brain/button';
import { hlm } from '@spartan-ng/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

import { injectBrnButtonConfig } from './hlm-button.token';

export const buttonVariants = cva(
  'disabled:bg-components-background-neutral-main disabled:text-components-content-neutral-quaternary outline-feedback-informative-main aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex cursor-pointer items-center justify-center gap-2 rounded-md whitespace-nowrap transition-all focus-visible:outline-2 disabled:pointer-events-none disabled:border-none [&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-components-background-primary-main text-components-content-neutral-primary-reversed hover:bg-components-background-primary-darker active:bg-components-background-primary-darkest',
        secondary:
          'text-components-content-neutral-secondary hover:text-components-content-neutral-primary hover:bg-components-background-neutral-lighter active:outline-stroke-neutral-dark outline-stroke-neutral-medium focus-visible:outline-components-focus bg-white outline hover:outline-2',
        ghost:
          'hover:bg-components-background-neutral-main active:bg-components-background-primary-lightest active:text-components-content-primary-darker text-components-content-neutral-secondary hover:text-components-content-neutral-primary bg-transparent disabled:bg-transparent',
        link: 'text-components-content-primary-main hover:text-components-content-primary-darker underline',
        zone: 'text-components-content-neutral-secondary border-stroke-neutral-dark component-small-medium gap-md outline-stroke-neutral-medium focus-visible:outline-components-focus hover:bg-components-background-neutral-lighter hover:text-components-content-neutral-primary w-full rounded-md border border-dashed focus-visible:bg-white focus-visible:outline-2',
        destructive:
          'bg-feedback-negative-main hover:bg-feedback-negative-darker active:bg-feedback-negative-darkest text-white',
      },
      size: {
        default: 'px-md py-[10px]',
        compact: 'p-0',
        icon: 'size-9',
        large: 'p-md',
        xxl: 'size-xxl',
      },
      text: {
        semibold: 'component-medium-semibold',
        medium: 'component-medium-medium',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      text: 'semibold',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: 'button[hlmBtn], a[hlmBtn]',
  exportAs: 'hlmBtn',
  hostDirectives: [{ directive: BrnButton, inputs: ['disabled'] }],
  host: {
    '[class]': '_computedClass()',
  },
})
export class HlmButton {
  private readonly _config = injectBrnButtonConfig();

  private readonly _additionalClasses = signal<ClassValue>('');

  public readonly userClass = input<ClassValue>('', { alias: 'class' });

  protected readonly _computedClass = computed(() =>
    hlm(
      buttonVariants({ variant: this.variant(), size: this.size(), text: this.text() }),
      this.userClass(),
      this._additionalClasses(),
    ),
  );

  public readonly variant = input<ButtonVariants['variant']>(this._config.variant);

  public readonly size = input<ButtonVariants['size']>(this._config.size);

  public readonly text = input<ButtonVariants['text']>();

  setClass(classes: string): void {
    this._additionalClasses.set(classes);
  }
}
